import { useState } from "react";
import { gtagReportConversion } from "@/lib/gtag";
import { SEOHead } from "@/components/SEOHead";
import { touristAttractionSchema, tubingServiceSchema, aggregateRatingFor } from "@/lib/schema";
import { Header } from "@/components/layout/Header";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getPostsBySlugs } from "@/content/blogPosts";
import { Footer } from "@/components/layout/Footer";
import { SEOContentBlock } from "@/components/seo/SEOContentBlock";
import { WhatsNearby } from "@/components/seo/WhatsNearby";
import { Testimonials } from "@/components/seo/Testimonials";
import { seoCopy } from "@/content/seoCopy";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { BookingPopupModal } from "@/components/home/BookingPopupModal";
import { TubingBookingPopupModal } from "@/components/home/TubingBookingPopupModal";
import { 
  Users, 
  Clock, 
  Waves, 
  MapPin, 
  CheckCircle, 
  Sun,
  LifeBuoy,
  Droplets,
  Mountain,
  Sparkles,
  Info,
  Thermometer,
  ShieldCheck,
  CircleDollarSign
} from "lucide-react";

// Images
import tubingHero from "@/assets/tubing-hero.jpg";
import friendsTubing from "@/assets/gallery/friends-tubing.jpg";
import tubingSelfie from "@/assets/gallery/tubing-selfie.jpg";
import tubingCanyonView from "@/assets/gallery/tubing-canyon-view.jpg";
import sonsTubesStack from "@/assets/gallery/sons-tubes-stack.jpg";
import kidWhaleFloat from "@/assets/gallery/kid-whale-float.jpg";
import tubingRiverside from "@/assets/tubing/tubing-riverside.jpg";
import coupleFloating from "@/assets/tubing/couple-floating.jpg";
import sonsTubesRiver from "@/assets/tubing/sons-tubes-river.jpg";
import groupFloating from "@/assets/tubing/group-floating.jpg";
import coupleTube from "@/assets/tubing/couple-tube.jpg";
import familyTubingFun from "@/assets/tubing/family-tubing-fun.jpg";
import guysWithTubes from "@/assets/tubing/guys-with-tubes.jpg";
import friendsFloatingCooler from "@/assets/tubing/friends-floating-cooler.jpg";
import sonsTubesStacked from "@/assets/tubing/sons-tubes-stacked.jpg";
import womanTubingRelaxed from "@/assets/tubing/woman-tubing-relaxed.webp";
import groupTubingFun from "@/assets/tubing/group-tubing-fun.webp";

// Badge images
import aerialProperty from "@/assets/tubing/aerial-property.jpg";
import familyTubes from "@/assets/tubing/family-tubes.jpg";
import friendsRiver from "@/assets/tubing/friends-river.jpg";
import kidTubing from "@/assets/tubing/kid-tubing.jpg";
import groupRiverFloat from "@/assets/tubing/group-river-float.jpg";
import friendsFloatingRiver from "@/assets/tubing/friends-floating-river.webp";
import canyonTubingScene from "@/assets/tubing/canyon-tubing-scene.jpg";
import kidsPlaying from "@/assets/tubing/kids-playing.jpg";

const tubingFeatures = [
  {
    icon: Clock,
    title: "2-3 Hour Float",
    description: "The perfect length to relax without getting bored or exhausted",
    image: friendsFloatingRiver,
    alt: "Friends drifting in a row of river tubes during a relaxed two- to three-hour float on the Guadalupe River"
  },
  {
    icon: Mountain,
    title: "Scenic Canyon Views",
    description: "Drift through stunning limestone canyon walls and lush scenery",
    image: canyonTubingScene,
    alt: "Tubers floating past tall limestone canyon walls lined with cypress trees on the Guadalupe River"
  },
  {
    icon: MapPin,
    title: "End at Your Cabana",
    description: "Float right back to your private basecamp — no shuttle needed",
    image: aerialProperty,
    alt: "Aerial view of Son's Guadalupe riverfront property showing private cabanas along the Guadalupe River tubing takeout"
  },
  {
    icon: LifeBuoy,
    title: "Quality Tubes Included",
    description: "Heavy-duty river tubes with handles and cup holders",
    image: familyTubes,
    alt: "Family seated in heavy-duty Son's river tubes with built-in cup holders and backrests on the Guadalupe"
  },
  {
    icon: Droplets,
    title: "Crystal Clear Water",
    description: "The Guadalupe's spring-fed waters stay refreshingly cool",
    image: friendsRiver,
    alt: "Friends standing waist-deep in the clear blue-green spring-fed water of the Guadalupe River holding tubes"
  },
  {
    icon: ShieldCheck,
    title: "Safe & Family-Friendly",
    description: "Calm waters perfect for all ages and skill levels",
    image: kidsPlaying,
    alt: "Children playing safely in the shallow calm section of the Guadalupe River wearing life jackets"
  }
];


const pricingOptions = [
  {
    title: "Single Tube",
    price: "$24.99",
    unit: "per person",
    description: "Individual river tube with backrest",
    features: ["Heavy-duty construction", "Built-in cup holder", "Comfortable backrest", "2-3 hour float time"]
  },
  {
    title: "Cooler Tube",
    price: "$19.99",
    unit: "per cooler",
    description: "Keep your drinks cold on the river",
    features: ["Fits standard coolers", "Tether rope included", "Keeps refreshments close", "Floats alongside you"]
  },
  {
    title: "Parking",
    price: "$24.99",
    unit: "per car",
    description: "You can book extra parking with your reservation or onsite.",
    features: []
  }
];

const includedWithCabana = [
  { icon: MapPin, text: "Float ends at your cabana" },
  { icon: Waves, text: "Private river access point" },
  { icon: Sun, text: "Shaded rest area waiting" },
  { icon: Thermometer, text: "Cold drinks in your cooler" }
];

const rules = [
  { icon: LifeBuoy, text: "Life jackets available (required for non-swimmers)", allowed: true },
  { icon: Users, text: "Children must be with adults", allowed: true },
  { icon: CircleDollarSign, text: "Cash & card accepted", allowed: true },
  { icon: Sparkles, text: "No glass containers on the river", allowed: false },
  { icon: Info, text: "No outside tubes (rentals required)", allowed: false }
];

const galleryImages = [
  { src: friendsTubing, alt: "Group of friends laughing while floating linked tubes down the Guadalupe River on a sunny afternoon" },
  { src: tubingSelfie, alt: "Two tubers taking a selfie from their river tubes with green riverbank trees behind them on the Guadalupe" },
  { src: tubingCanyonView, alt: "View from a river tube looking down the Guadalupe River toward limestone canyon bluffs and overhanging cypress trees" },
  { src: kidWhaleFloat, alt: "Young child sitting on a blue whale-shaped float being pulled along the calm Guadalupe River" },
  { src: sonsTubesStack, alt: "Tall stack of black Son's river tubes ready for rent at the Son's Guadalupe tubing launch" },
  { src: tubingRiverside, alt: "Group relaxing on the rocky bank of the Guadalupe River with rented tubes beside them" },
  { src: coupleFloating, alt: "Couple lying back on two river tubes drifting side by side on the clear Guadalupe River" },
  { src: sonsTubesRiver, alt: "Stack of Son's branded river tubes lined up along the Guadalupe River launch ramp" },
  { src: groupFloating, alt: "Large group of tubers floating together in a chain down a calm stretch of the Guadalupe River" },
  { src: coupleTube, alt: "Couple sharing a single double river tube while floating the Guadalupe River near New Braunfels" },
  { src: familyTubingFun, alt: "Family with kids splashing and smiling while tubing together on the Guadalupe River" },
  { src: guysWithTubes, alt: "Group of young men holding river tubes overhead while wading into the Guadalupe River" },
  { src: friendsFloatingCooler, alt: "Friends floating the Guadalupe River with a cooler tube tethered between them holding drinks" },
  { src: sonsTubesStacked, alt: "Pyramid stack of Son's Guadalupe rental tubes set up on the grassy riverbank" },
  { src: womanTubingRelaxed, alt: "Woman reclining in a river tube with sunglasses on, drifting peacefully down the Guadalupe River" },
  { src: groupTubingFun, alt: "Group of friends cheering and toasting drinks while floating together on the Guadalupe River" }
];


const faqs = [
  {
    question: "How long is the tubing float?",
    answer: "The float takes approximately 2-3 hours depending on water levels and how leisurely you take it. It's the perfect length to enjoy the scenery without getting too tired or sunburned."
  },
  {
    question: "Do I need to be a strong swimmer?",
    answer: "No, the river is calm and shallow in most areas and we provide FREE life jackets to use which is required for non or weak swimmers. Children should always be accompanied by adults."
  },
  {
    question: "Can I bring my own tube?",
    answer: "Outside tubes are not permitted on our property. We provide high-quality, heavy-duty tubes for rent to ensure safety and a great experience for everyone."
  },
  {
    question: "What should I bring?",
    answer: "Sunscreen, sunglasses with a strap, water shoes or sandals that stay on, and a waterproof phone case. Skip the glass containers — cans and plastic only on the river."
  },
  {
    question: "What if I don't have a cabana?",
    answer: "That's perfectly fine, you don't have to have a cabana or picnic table reserved to float with us though having one is the beauty of our setup, in that you float right back to your private cabana at the end!"
  },
  {
    question: "Is tubing safe for kids?",
    answer: "Yes, as long as an adult is supervising and kids have life jackets."
  },
  {
    question: "What happens if the weather is bad?",
    answer: "Light rain doesn't stop the fun — you're already wet! For severe weather (lightning, flash flooding), we'll pause operations for safety. Check our cancellation policy for refund details."
  }
];

const Tubing = () => {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [tubingOpen, setTubingOpen] = useState(false);
  
  return (
    <div className="min-h-screen">


      <SEOHead
        title="Tubing the Guadalupe River, New Braunfels"
        description="Family-friendly tubing on the Guadalupe River near New Braunfels. Reserve tubes online and float from your private cabana — no shuttle hassles."
        canonical="/tubing"
        ogImage="/og/tubing.jpg"
        schemaJson={[
          touristAttractionSchema(
            "Tubing the Guadalupe River Near New Braunfels",
            "Family-friendly tubing on the Guadalupe River near New Braunfels, TX with cabana rentals, kayaks, and direct river access.",
          ),
          { ...tubingServiceSchema("https://sonsguadalupe.com/tubing"), aggregateRating: aggregateRatingFor("tubing") },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.question,
              acceptedAnswer: { "@type": "Answer", text: f.answer },
            })),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://sonsguadalupe.com/" },
              { "@type": "ListItem", position: 2, name: "Tubing", item: "https://sonsguadalupe.com/tubing" },
            ],
          },
        ]}
      />
      <BookingPopupModal open={bookingOpen} onOpenChange={setBookingOpen} />
      <TubingBookingPopupModal open={tubingOpen} onOpenChange={setTubingOpen} />
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center">
        <div className="absolute inset-0">
          <img fetchPriority="high" loading="eager" decoding="async"
            src={tubingHero}
            alt="Family floating in linked river tubes down the clear, spring-fed Guadalupe River with limestone canyon walls in the background near New Braunfels, Texas"
            className="w-full h-full object-cover"
            
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/70 via-charcoal/50 to-transparent" />
        </div>
        
        <div className="relative z-10 container-narrow mx-auto px-4 md:px-8 pt-32 pb-16">
          <div className="max-w-2xl">
            <p className="text-coral-orange font-medium mb-4 text-lg">Tubing Experience</p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              The Float That Ends at Your Doorstep
            </h1>
            <p className="text-white/90 text-lg md:text-xl mb-8">
              2-3 hours drifting through limestone canyons on crystal-clear water — then float right back to YOUR private cabana. No shuttles, no crowds, no stress.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button variant="hero" size="xl" onClick={() => { gtagReportConversion(); setTubingOpen(true); }}>
                Go Tubing — $24.99/person
              </Button>
              <Button variant="hero-outline" size="xl" onClick={() => { gtagReportConversion(); setBookingOpen(true); }}>
                Book a Cabana First
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="section-padding bg-cream">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Why Our Tubing Experience is Different
            </h2>
            <p className="text-charcoal/70 text-lg max-w-2xl mx-auto">
              Forget crowded public access points and sketchy shuttle rides. This is tubing done right.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tubingFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-elevated transition-shadow"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={feature.image}
                    loading="lazy"
                    alt={feature.alt}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="w-10 h-10 bg-teal/10 rounded-xl flex items-center justify-center mb-3">
                    <feature.icon className="w-5 h-5 text-teal" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-charcoal mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-charcoal/70">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="section-padding bg-white">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-4">
              THE BEST FLOAT on the Guad!
            </h2>
            <p className="text-charcoal/70 text-lg max-w-3xl mx-auto">
              Come enjoy a beautiful day on the gorgeous Guadalupe River tubing with friends and family! With hundreds of tubes and brand-new shuttle vans we can get you on the river and having fun in no time!
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.map((image, index) => (
              <div 
                key={index}
                className={`relative overflow-hidden rounded-xl ${
                  index === 0 || index === 7 ? 'col-span-2 row-span-2' : ''
                }`}
              >
                <img
                  loading="lazy"
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover aspect-square hover:scale-105 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="section-padding bg-teal text-white">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              No hidden fees. Just grab your tube and go.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {pricingOptions.map((option, index) => (
              <div 
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20"
              >
                <h3 className="font-heading text-xl font-bold mb-2">{option.title}</h3>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-coral-orange">{option.price}</span>
                  <span className="text-white/70 ml-2">{option.unit}</span>
                </div>
                <p className="text-white/80 mb-6">{option.description}</p>
                <ul className="space-y-2">
                  {option.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-white/90 text-sm">
                      <CheckCircle className="w-4 h-4 text-coral-orange flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          <div className="text-center flex flex-col items-center gap-3">
            <Button 
              variant="hero" 
              size="xl" 
              onClick={() => setTubingOpen(true)}
              className="bg-coral-orange hover:bg-coral-orange/90"
            >
              Book Now
            </Button>
            <button 
              onClick={() => setBookingOpen(true)}
              className="bg-coral-orange text-white px-6 py-2 rounded-full font-bold text-sm md:text-base shadow-lg hover:bg-coral-orange/90 transition-colors cursor-pointer"
            >
              🎉 Save! - 25% OFF Tubing When You Rent a Cabana
            </button>
          </div>
        </div>
      </section>

      {/* The Perfect Combo */}
      <section className="section-padding bg-cream">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src={tubingCanyonView}
                alt="Tubers drifting through a shaded limestone canyon section of the Guadalupe River near Son's Guadalupe cabanas"
                className="rounded-2xl shadow-elevated"
                loading="lazy"
              />
            </div>
            
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-6">
                The Perfect Combo: Cabana + Tubing
              </h2>
              <p className="text-charcoal/70 text-lg mb-8">
                Here's what makes our tubing experience unique: you float right back to your <Link to="/cabanas" className="text-teal underline-offset-2 hover:underline font-medium">private cabana</Link>. No waiting for shuttles, no hiking back to a parking lot. Just drift back to your <Link to="/book-now" className="text-teal underline-offset-2 hover:underline font-medium">shaded home base</Link> where your cooler, grill, and comfy chairs are waiting.
              </p>

              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {includedWithCabana.map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-teal" />
                    </div>
                    <span className="text-charcoal/80">{item.text}</span>
                  </div>
                ))}
              </div>
              
              <div className="flex flex-col items-start gap-3">
                <Button variant="default" size="lg" onClick={() => setBookingOpen(true)}>
                  Book Cabana + Tubing Package
                </Button>
                <button 
                  onClick={() => setBookingOpen(true)}
                  className="bg-coral-orange text-white px-6 py-2 rounded-full font-bold text-sm md:text-base shadow-lg hover:bg-coral-orange/90 transition-colors cursor-pointer"
                >
                  🎉 Save! - 25% OFF Tubing When You Rent a Cabana
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rules & Safety */}
      <section className="section-padding bg-white">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-4">
                River Rules & Safety
              </h2>
              <p className="text-charcoal/70 text-lg">
                A few guidelines to keep everyone safe and having fun.
              </p>
            </div>
            
            <div className="bg-cream rounded-2xl p-8">
              <div className="space-y-4">
                {rules.map((rule, index) => (
                  <div key={index} className="flex items-center gap-4 py-3 border-b border-border last:border-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      rule.allowed ? 'bg-deep-green/10' : 'bg-coral-orange/10'
                    }`}>
                      {rule.allowed ? (
                        <CheckCircle className="w-5 h-5 text-deep-green" />
                      ) : (
                        <Info className="w-5 h-5 text-coral-orange" />
                      )}
                    </div>
                    <span className="text-charcoal">{rule.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section-padding bg-cream">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Tubing FAQs
            </h2>
          </div>
          
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <details 
                key={index}
                className="group bg-white rounded-xl p-6 cursor-pointer shadow-soft"
              >
                <summary className="flex items-center justify-between font-heading font-bold text-charcoal text-lg list-none">
                  {faq.question}
                  <Sun className="w-5 h-5 text-teal group-open:rotate-45 transition-transform" />
                </summary>
                <p className="mt-4 text-charcoal/70 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
          <p className="max-w-3xl mx-auto text-center text-charcoal/70 mt-8">
            Helpful links: <Link to="/cabanas" className="text-teal underline-offset-2 hover:underline font-medium">cabana rentals</Link>, <Link to="/cabana-vs-picnic-table" className="text-teal underline-offset-2 hover:underline font-medium">cabana vs picnic table</Link>, <Link to="/faqs" className="text-teal underline-offset-2 hover:underline font-medium">all FAQs</Link>, and our <Link to="/cancellation" className="text-teal underline-offset-2 hover:underline font-medium">cancellation policy</Link>.
          </p>

        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-deep-green text-white">
        <div className="container-narrow mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Ready to Float the Guadalupe?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Book your cabana and add tubing for the ultimate river day experience. No crowds, no shuttles — just pure relaxation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="hero" size="xl" onClick={() => setBookingOpen(true)}>
              Book Your River Day
            </Button>
            <Button variant="hero-outline" size="xl" className="border-white text-white hover:bg-white/10">
              Call 877-577-7667
            </Button>
          </div>
          <p className="text-white/80 mt-6">
            Prefer overnight? Check out <Link to="/glamping" className="underline underline-offset-2 hover:text-white font-medium">glamping</Link> or <Link to="/group-bookings" className="underline underline-offset-2 hover:text-white font-medium">group bookings</Link>.
          </p>

        </div>
      </section>

      <section aria-labelledby="tubing-guide-heading" className="bg-cream py-16 md:py-20">
        <div className="container max-w-4xl mx-auto px-4">
          <h2 id="tubing-guide-heading" className="font-display text-3xl md:text-4xl font-bold text-primary mb-6">
            Everything you need to know about tubing the Guadalupe at Son's
          </h2>

          <div className="space-y-5 text-charcoal/85 leading-relaxed text-base md:text-lg">
            <p>
              Tubing the Guadalupe River near <Link to="/blog/river-road-new-braunfels-guide" className="text-teal underline-offset-2 hover:underline font-medium">New Braunfels</Link> is one of the great Texas summer traditions, and we've built our setup at Son's to make it as easy as humanly possible. Instead of guessing which public lot will fill up first, parking a mile from your put-in, and praying your shuttle shows up on time, our day-rental guests start and end their float at the property. You park onsite, walk to the river, float a calm and family-friendly stretch, and step out steps from your <Link to="/cabanas" className="text-teal underline-offset-2 hover:underline font-medium">cabana</Link> or <Link to="/cabana-vs-picnic-table" className="text-teal underline-offset-2 hover:underline font-medium">picnic table</Link>.
            </p>
            <p>
              We intentionally cap daily capacity so the river never feels packed. That means quieter floats, less litter, and a much better experience for kids and first-time tubers. Tube rentals start at $24.99, cooler tubes are available, and life jackets are recommended for non-swimmers and easy to bring along. Every booking includes a shuttle so you can float multiple times during the day without having to coordinate a separate ride.
            </p>
            <p>
              <strong className="text-primary">What's included:</strong> tube rental, shuttle service, river access, and use of the property's restrooms and changing areas. <strong className="text-primary">What to bring:</strong> swimsuits, water shoes (the bottom can be rocky in spots), sunscreen, a refillable water bottle, and a small cooler with non-glass drinks and snacks. <strong className="text-primary">What to leave at home:</strong> glass containers and styrofoam — both are not allowed on the river under Texas state law. Bluetooth speakers should be kept at a reasonable volume out of respect for other floaters.
            </p>
            <p>
              The <Link to="/blog/best-time-to-tube-guadalupe-river" className="text-teal underline-offset-2 hover:underline font-medium">best tubing months on the Guadalupe</Link> are typically May through early September, with June and July hitting the sweet spot for warm water and reliable flow. Spring breakers and Labor Day weekenders should book as far in advance as possible — those weekends sell out every year. If river flow is too low or too high to float safely, we'll reach out directly about rescheduling under our <Link to="/cancellation" className="text-teal underline-offset-2 hover:underline font-medium">weather policy</Link>.
            </p>

          </div>

          <div className="mt-10">
            <h3 className="font-display text-2xl md:text-3xl font-bold text-primary mb-5">
              Tubing FAQs
            </h3>
            <div className="space-y-5">
              <div>
                <p className="font-semibold text-charcoal">When is the best time of year to tube?</p>
                <p className="text-charcoal/80 mt-1">May through early September is peak season, with June and July offering the warmest, most reliable conditions. See our <Link to="/blog/best-time-to-tube-guadalupe-river" className="text-teal underline-offset-2 hover:underline font-medium">month-by-month guide on the blog</Link> for details.</p>
              </div>
              <div>
                <p className="font-semibold text-charcoal">Is tubing safe for kids?</p>
                <p className="text-charcoal/80 mt-1">Yes — the stretch in front of Son's is calm and family-friendly. We strongly recommend life jackets for non-swimmers and kids under 12. See our <Link to="/blog/tubing-with-kids-guadalupe-river" className="text-teal underline-offset-2 hover:underline font-medium">tubing with kids guide</Link> for tips.</p>
              </div>
              <div>
                <p className="font-semibold text-charcoal">Do I need to bring my own tube?</p>
                <p className="text-charcoal/80 mt-1">No. <Link to="/blog/guadalupe-river-tube-rental-near-new-braunfels" className="text-teal underline-offset-2 hover:underline font-medium">Tube and cooler-tube rentals</Link> are available at checkout and waiting for you onsite. Shuttle service is included with every rental.</p>
              </div>
              <div>
                <p className="font-semibold text-charcoal">What happens if the weather is bad?</p>
                <p className="text-charcoal/80 mt-1">Light rain rarely closes the river. If flow or weather requires us to pause floats, we'll contact you directly and work with you on a reschedule — see our <Link to="/cancellation" className="text-teal underline-offset-2 hover:underline font-medium">cancellation policy</Link> for full terms.</p>
              </div>
            </div>
            <p className="mt-6 text-charcoal/80">
              More questions? Visit our full <Link to="/faqs" className="text-teal underline-offset-2 hover:underline font-medium">FAQs</Link> or explore <Link to="/cabanas" className="text-teal underline-offset-2 hover:underline font-medium">cabana rentals</Link> and <Link to="/glamping" className="text-teal underline-offset-2 hover:underline font-medium">glamping</Link>.
            </p>

          </div>
        </div>
      </section>

      <WhatsNearby />


      <Testimonials category="tubing" />
      <SEOContentBlock {...seoCopy.tubing} />
      <RelatedReads posts={getPostsBySlugs(["tubing-guadalupe-river-new-braunfels", "best-time-to-tube-guadalupe-river", "what-to-pack-tubing-guadalupe-river"])} heading="From our blog" />
      <Footer />
    </div>
  );
};

export default Tubing;
